/**
 * usePlacementEdit — controller for an in-context placement edit. Loads the
 * frozen artwork, seeds the shared `placementEditStore`, runs the debounced
 * authoritative backend preview, and commits. Surface layers drag the artwork
 * (writing ax/ay to the store); this hook owns rotation / opacity / bg / commit.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  artworkApi, ArtworkApiError, artworkCodeLabel,
  type ArtworkPublic, type PlacementPreview,
} from "../../lib/artwork-api";
import { placementEditStore } from "./placementEditStore";

export function usePlacementEditState() {
  return useSyncExternalStore(placementEditStore.subscribe, placementEditStore.get, placementEditStore.get);
}

export function usePlacementEdit(artworkId: string, onCommitted?: () => void, onClose?: () => void) {
  const state = usePlacementEditState();
  const [art, setArt] = useState<ArtworkPublic | null>(null);
  const [preview, setPreview] = useState<PlacementPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const baseRevisionRef = useRef(1);
  const idemRef = useRef<string>(`pl-${Math.random().toString(36).slice(2)}-${Date.now()}`);

  // load + seed store
  useEffect(() => {
    const c = new AbortController();
    setError(null);
    artworkApi.getArtwork(artworkId, c.signal).then((a) => {
      if (c.signal.aborted) return;
      setArt(a);
      const p = a.placement;
      baseRevisionRef.current = p?.revision ?? 1;
      placementEditStore.start({
        artworkId: a.artwork_id,
        ax: p?.anchor_x ?? 0,
        ay: p?.anchor_y ?? 0,
        rotDeg: Math.round((p?.rotation_millideg ?? (p?.rotation ?? 0) * 1000) / 1000) % 360,
        bg: p?.background ?? null,
        opacityPct: Math.round((p?.empty_opacity_bps ?? 0) / 100),
        width: a.width,
        height: a.height,
        content: a.content,
        valid: false,
      });
    }).catch((e) => { if (!c.signal.aborted) setError(e instanceof ArtworkApiError ? e.message : "Failed to load artwork"); });
    return () => { c.abort(); placementEditStore.stop(artworkId); };
  }, [artworkId]);

  // debounced authoritative preview
  useEffect(() => {
    if (!state || !art) return;
    const c = new AbortController();
    const t = setTimeout(() => {
      artworkApi.previewPlacement(artworkId, {
        anchor_x: state.ax, anchor_y: state.ay, rotation_millideg: state.rotDeg * 1000,
        background: state.bg, empty_opacity_bps: state.opacityPct * 100,
      }, c.signal).then((p) => {
        setPreview(p);
        placementEditStore.update({ valid: !!p.can_commit });
      }).catch((e) => { if (!c.signal.aborted) setError(e instanceof ArtworkApiError ? e.message : "Preview failed"); });
    }, 160);
    return () => { clearTimeout(t); c.abort(); };
  }, [artworkId, art, state?.ax, state?.ay, state?.rotDeg, state?.bg, state?.opacityPct]);

  const commit = useCallback(async () => {
    if (!state) return;
    setCommitting(true); setError(null);
    try {
      await artworkApi.commitPlacement(artworkId, {
        anchor_x: state.ax, anchor_y: state.ay, rotation_millideg: state.rotDeg * 1000,
        background: state.bg, empty_opacity_bps: state.opacityPct * 100,
        expected_revision: baseRevisionRef.current, idempotency_key: idemRef.current,
      });
      onCommitted?.();
      placementEditStore.stop(artworkId);
      onClose?.();
    } catch (e) {
      setError(e instanceof ArtworkApiError ? artworkCodeLabel(e.code) : "Commit failed");
      idemRef.current = `pl-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    } finally {
      setCommitting(false);
    }
  }, [artworkId, state, onCommitted, onClose]);

  const cancel = useCallback(() => { placementEditStore.stop(artworkId); onClose?.(); }, [artworkId, onClose]);

  const valid = !!(preview?.can_commit);
  const reasons = (preview?.warnings ?? []).map(artworkCodeLabel);

  return {
    state, art, preview, valid, reasons, error, committing,
    setRot: (v: number) => placementEditStore.update({ rotDeg: v }),
    setOpacity: (v: number) => placementEditStore.update({ opacityPct: v }),
    setBg: (v: string | null) => placementEditStore.update({ bg: v }),
    commit, cancel,
  };
}
